import { useState, useEffect, useCallback } from 'react';
import useApi from '../useApi';
import axiosInstance from '../axiosInstance';

const BASE = '/geo/airports';

export const getAirports = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const getAirportById = (id) =>
  axiosInstance.get(`${BASE}/${id}`).then((res) => res.data);

export const createAirport = (data) =>
  axiosInstance.post(BASE, data).then((res) => res.data);

export const updateAirport = (id, data) =>
  axiosInstance.put(`${BASE}/${id}`, data).then((res) => res.data);

export const deleteAirport = (id) =>
  axiosInstance.delete(`${BASE}/${id}`).then((res) => res.data);

export const toggleAirportActive = (id) =>
  axiosInstance.patch(`${BASE}/${id}/toggle-active`).then((res) => res.data);

/**
 * useGetAirports - fetch airports list with params
 * @param {Object} params - { page, limit, search, countryId, type, active, sortBy, sortOrder }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetAirports = (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    countryId = '',
    type = '',
    active = '',
    sortBy = 'name',
    sortOrder = 'ASC',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAirports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit, sortBy, sortOrder };
      if (search) queryParams.search = search;
      if (countryId) queryParams.countryId = countryId;
      if (type) queryParams.type = type;
      if (active !== '') queryParams.active = active;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load airports');
        setData([]);
      }
    } catch (err) {
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).');
      } else {
        setError(err.message || 'Failed to load airports');
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, countryId, type, active, sortBy, sortOrder]);

  useEffect(() => {
    fetchAirports();
  }, [fetchAirports]);

  const refetch = useCallback(() => {
    fetchAirports();
  }, [fetchAirports]);

  return { data, pagination, loading, error, refetch };
};

export const useCreateAirport = () => {
  return useApi(BASE, { method: 'POST' });
};

/**
 * useUpdateAirport - update airport by id
 * @returns { execute(id, payload), loading, error }
 */
export const useUpdateAirport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update airport';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useDeleteAirport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete airport';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useToggleAirportActive = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BASE}/${id}/toggle-active`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to toggle status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};
